import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  SmartPhone01Icon, 
  ComputerIcon, 
  Location01Icon, 
  Clock01Icon, 
  Delete02Icon, 
  Alert01Icon, 
  ArrowLeft01Icon
} from '@hugeicons/core-free-icons';
import Button from '../components/Button';
import ResponsiveModal from '../components/ResponsiveModal';
import { api } from '../utils/api';
import { FLAGS } from '../config/flags';

const Sessions = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [terminatingId, setTerminatingId] = useState(null);
  const [showConfirmModal, setShowConfirmConfirmModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const navigate = useNavigate();

  const fetchSessions = async () => {
    if (FLAGS.USE_MOCK_API) {
      setSessions([
        { id: '1', ip_address: '192.168.1.1', user_agent: 'Chrome on MacBook Pro', created_at: new Date().toISOString(), is_current: true },
        { id: '2', ip_address: '10.0.0.1', user_agent: 'Safari on iPhone', created_at: new Date(Date.now() - 86400000).toISOString(), is_current: false },
      ]);
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/sessions');
      // Handle both { data: { sessions: [] } } and { data: [] }
      const sessionsList = response.data?.sessions || response.data || [];
      setSessions(Array.isArray(sessionsList) ? sessionsList : []);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
      setError(err.message || 'Failed to load active sessions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const handleTerminate = async () => {
    if (terminatingId === 'all') {
      try {
        await api.delete('/sessions/all');
        setSessions(sessions.filter(s => s.is_current));
        setShowConfirmConfirmModal(false);
      } catch (err) {
        alert(err.message);
      } finally {
        setTerminatingId(null);
      }
      return;
    }

    if (!selectedSession) return;
    setTerminatingId(selectedSession.id);
    
    try {
      await api.delete(`/sessions/${selectedSession.id}`);
      setSessions(sessions.filter(s => s.id !== selectedSession.id));
      setShowConfirmConfirmModal(false);
      setSelectedSession(null);
    } catch (err) {
      alert(err.message);
    } finally {
      setTerminatingId(null);
    }
  };

  const confirmTerminate = (session) => {
    setSelectedSession(session);
    setTerminatingId(session?.id || 'all');
    setShowConfirmConfirmModal(true);
  };

  const getDeviceIcon = (userAgent) => {
    if (!userAgent) return ComputerIcon;
    const ua = userAgent.toLowerCase();
    if (ua.includes('mobile') || ua.includes('iphone') || ua.includes('android')) return SmartPhone01Icon;
    return ComputerIcon;
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-soft-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-20">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white/10 backdrop-blur-xl p-8 rounded-[2.5rem] border border-white/20 relative overflow-hidden">
        <div className="relative z-10">
          <h1 className="text-4xl font-black text-charcoal dark:text-white tracking-tight">Active Sessions</h1>
          <p className="text-muted-gray dark:text-gray-300 mt-2 font-medium text-lg">Manage devices logged into your account.</p>
        </div>
        {sessions.length > 1 && (
          <div className="relative z-10">
            <Button 
              variant="danger" 
              onClick={() => confirmTerminate(null)}
              className="px-8 py-4 rounded-2xl text-sm font-black bg-white dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-100 dark:border-red-500/20 hover:bg-red-50 transition-all shadow-sm"
            >
              <HugeiconsIcon icon={Delete02Icon} size={18} className="mr-2" />
              Log Out All Others
            </Button>
          </div>
        )}
      </div>

      <div className="max-w-4xl mx-auto w-full space-y-6">
        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-2xl border border-red-100 dark:border-red-800 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {sessions.length === 0 && !error && (
            <div className="text-center py-20 bg-white/50 dark:bg-dark-surface/50 rounded-[2.5rem] border border-dashed border-light-gray/20">
              <p className="text-muted-gray font-bold">No active sessions found.</p>
            </div>
          )}
          {sessions.map((session) => (
            <div 
              key={session.id} 
              className={`p-6 sm:p-8 rounded-[2.5rem] border transition-all shadow-sm ${
                session.is_current 
                  ? 'border-soft-green/50 bg-soft-green/5' 
                  : 'border-light-gray/20 dark:border-dark-border/20 bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md'
              }`}
            >
              <div className="flex flex-col sm:flex-row items-start justify-between gap-6">
                <div className="flex items-start gap-5 flex-1 min-w-0">
                  <div className={`p-4 rounded-2xl shrink-0 ${session.is_current ? 'bg-soft-green/20 text-soft-green' : 'bg-gray-100 dark:bg-gray-800 text-muted-gray'}`}>
                    <HugeiconsIcon icon={getDeviceIcon(session.user_agent)} size={28} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="font-black text-charcoal dark:text-white text-xl tracking-tight">
                        {session.ip_address}
                      </h3>
                      {session.is_current && (
                        <span className="bg-soft-green text-white text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest shrink-0 shadow-sm">
                          Current Device
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-gray mb-4 font-medium break-all">{session.user_agent || 'Unknown Browser'}</p>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-muted-gray font-bold uppercase tracking-widest">
                      <span className="flex items-center gap-2">
                        <HugeiconsIcon icon={Clock01Icon} size={14} className="text-soft-green" />
                        {new Date(session.created_at).toLocaleDateString()}
                      </span>
                      {session.location && (
                        <span className="flex items-center gap-2">
                          <HugeiconsIcon icon={Location01Icon} size={14} className="text-soft-green" />
                          {session.location}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {!session.is_current && (
                  <button 
                    onClick={() => confirmTerminate(session)}
                    className="w-full sm:w-auto px-6 py-4 bg-red-50 dark:bg-red-900/20 text-red-500 rounded-2xl hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2"
                  >
                    <HugeiconsIcon icon={Delete02Icon} size={18} />
                    Revoke Access
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        
        <div className="pt-6">
          <button 
            onClick={() => navigate('/profile')}
            className="flex items-center text-muted-gray hover:text-soft-green font-black text-xs uppercase tracking-widest transition-colors"
          >
            <HugeiconsIcon icon={ArrowLeft01Icon} size={18} className="mr-2" />
            Back to Profile
          </button>
        </div>
      </div>

      <ResponsiveModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmConfirmModal(false)}
        title={terminatingId === 'all' ? "Log Out All Devices?" : "Revoke Session?"}
      >
        <div className="py-6 text-center space-y-6">
          <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-full inline-block text-red-500">
            <HugeiconsIcon icon={Alert01Icon} size={48} />
          </div>
          
          <p className="text-muted-gray px-4">
            {terminatingId === 'all' 
              ? "This will sign you out of all other devices and browsers. You will remain logged in on this device."
              : "Are you sure you want to log out of this device? The user will need to sign in again."
            }
          </p>

          <div className="flex gap-3 pt-2">
            <Button 
              variant="secondary" 
              onClick={() => setShowConfirmConfirmModal(false)}
              className="flex-1 py-4 rounded-2xl font-bold"
            >
              Cancel
            </Button>
            <Button 
              variant="danger" 
              onClick={handleTerminate}
              className="flex-1 py-4 rounded-2xl font-black bg-red-500 hover:bg-red-600 text-white border-none"
            >
              {terminatingId === 'all' ? "Log Out All" : "Revoke Access"}
            </Button>
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
};

export default Sessions;
